/**
 * KRA Photo Journal – AppHeader
 *
 * Top bar with logo, app name, logged-in user, and optional admin controls.
 */

import React from "react";

interface AppHeaderProps {
  currentUser?: string | null;
  onLogout?: () => void;
  /** If provided, shows an admin panel button (admin only) */
  onAdminPanel?: () => void;
  /** If provided, shows a bulk import button (admin only) */
  onBulkImport?: () => void;
  /** If provided, shows a bulk-sort button (admin only) */
  onBulkSort?: () => void;
  /** If provided, shows an auto-tag button (admin only) */
  onAutoTag?: () => void;
  /** If provided, shows a collections button (admin only) */
  onCollections?: () => void;
  /** Currently active collection ID */
  activeCollectionId?: string | null;
  /** Clear active collection filter */
  onClearCollection?: () => void;
  /** Kiosk / read-only mode — show guest badge instead of user controls */
  isReadOnly?: boolean;
}

export function AppHeader({
  currentUser,
  onLogout,
  onAdminPanel,
  onBulkImport,
  onBulkSort,
  onAutoTag,
  onCollections,
  activeCollectionId,
  onClearCollection,
  isReadOnly,
}: AppHeaderProps) {
  return (
    <header className="px-5 pt-5 pb-4 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <h1 className="text-lg font-semibold text-ink-0 tracking-tight">
            KRA
          </h1>
          <span className="text-lg text-ink-2 font-light tracking-tight">
            Photo Journal
          </span>
        </div>

        {currentUser && (
          <div className="flex items-center gap-2">
            {isReadOnly ? (
              <span className="text-2xs text-ink-3 bg-surface-2 px-2 py-0.5 rounded-full">
                Chế độ xem
              </span>
            ) : (
              <span className="text-2xs text-ink-2 hidden sm:block">{currentUser}</span>
            )}

            {onCollections && (
              <button
                onClick={onCollections}
                title="Collections"
                aria-label="Quản lý collections"
                className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors ${
                  activeCollectionId
                    ? "text-[#B76E6E] bg-[#B76E6E]/10 hover:bg-[#B76E6E]/20"
                    : "text-ink-2 hover:text-ink-0 hover:bg-surface-2"
                }`}
              >
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                <span className="hidden sm:inline">
                  {activeCollectionId ? "Collection" : "Collections"}
                </span>
              </button>
            )}

            {activeCollectionId && onClearCollection && (
              <button
                onClick={onClearCollection}
                title="Tắt filter collection"
                className="text-[#B76E6E] hover:text-white text-xs px-1.5 py-1 rounded-lg hover:bg-[#B76E6E]/20 transition-colors"
              >
                ×
              </button>
            )}

            {onBulkImport && (
              <button
                onClick={onBulkImport}
                title="Thêm bộ ảnh — gắn link Drive vào hệ thống, không di chuyển folder"
                aria-label="Thêm bộ ảnh vào hệ thống"
                className="text-ink-2 hover:text-ink-0 transition-colors text-xs px-2.5 py-1.5 rounded-lg hover:bg-surface-2 flex items-center gap-1.5"
              >
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span className="hidden sm:inline">Thêm bộ ảnh</span>
              </button>
            )}

            {onBulkSort && (
              <button
                onClick={onBulkSort}
                title="Sắp xếp — dọn folder &quot;01. Chưa phân loại&quot; vào đúng loại kimono (di chuyển trên Drive)"
                aria-label="Sắp xếp bộ ảnh mới tải xuống"
                className="text-ink-2 hover:text-ink-0 transition-colors text-xs px-2.5 py-1.5 rounded-lg hover:bg-surface-2 flex items-center gap-1.5"
              >
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M7 12h10" />
                  <path d="M11 18h2" />
                </svg>
                <span className="hidden sm:inline">Sắp xếp</span>
              </button>
            )}

            {onAutoTag && (
              <button
                onClick={onAutoTag}
                title="Auto tag"
                aria-label="Auto tag từ folder name + AI"
                className="text-ink-2 hover:text-ink-0 transition-colors text-xs px-2.5 py-1.5 rounded-lg hover:bg-surface-2 flex items-center gap-1.5"
              >
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
                <span className="hidden sm:inline">Auto Tag</span>
              </button>
            )}

            {onAdminPanel && (
              <button
                onClick={onAdminPanel}
                title="Admin panel"
                aria-label="Mở admin panel"
                className="text-ink-2 hover:text-ink-0 transition-colors text-xs px-2.5 py-1.5 rounded-lg hover:bg-surface-2 flex items-center gap-1.5"
              >
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                <span className="hidden sm:inline">Link tạm</span>
              </button>
            )}

            {!isReadOnly && onLogout && (
              <button
                onClick={onLogout}
                title="Đăng xuất"
                aria-label="Đăng xuất"
                className="text-ink-3 hover:text-ink-1 transition-colors text-xs px-2 py-1.5 rounded-lg hover:bg-surface-2"
              >
                Đăng xuất
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
